import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../api/client';

interface ZoomPlayerProps {
  meetingNumber: string;
  userName: string;
  userEmail?: string;
  joinUrl?: string;
}

type ZoomState = 'idle' | 'loading' | 'active' | 'error';

export default function ZoomPlayer({ meetingNumber, userName, userEmail, joinUrl }: ZoomPlayerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<ZoomState>('idle');
  const [error, setError] = useState('');

  // Parse token (tk=) and password (pwd=) from joinUrl
  const urlParams = (() => {
    if (!joinUrl) return { tk: '', pwd: '' };
    try {
      const u = new URL(joinUrl);
      return { tk: u.searchParams.get('tk') ?? '', pwd: u.searchParams.get('pwd') ?? '' };
    } catch { return { tk: '', pwd: '' }; }
  })();

  const join = async () => {
    setState('loading');
    setError('');

    try {
      const sigRes = await apiClient.post('/portal/zoom-signature', { meetingNumber, role: 0 });
      const { signature, sdkKey } = sigRes.data;

      // Classic (client view) SDK — renders fullscreen in #zmmtg-root, same tab
      const { ZoomMtg } = await import('@zoom/meetingsdk') as any;

      ZoomMtg.setZoomJSLib('https://source.zoom.us/5.1.4/lib', '/av');
      ZoomMtg.preLoadWasm();
      ZoomMtg.prepareWebSDK();

      // Show the SDK root
      const root = document.getElementById('zmmtg-root');
      if (root) {
        root.style.display = 'block';
        root.style.position = 'fixed';
        root.style.inset = '0';
        root.style.zIndex = '9999';
      }

      ZoomMtg.init({
        leaveUrl: window.location.href,
        disablePreview: false,
        success: () => {
          ZoomMtg.join({
            signature,
            sdkKey,
            meetingNumber: String(meetingNumber),
            userName,
            userEmail: userEmail ?? '',
            passWord: urlParams.pwd,
            tk: urlParams.tk,
            success: () => setState('active'),
            error: (e: any) => {
              setError(e?.errorMessage ?? e?.reason ?? 'Errore nel join.');
              setState('error');
              const root = document.getElementById('zmmtg-root');
              if (root) root.style.display = 'none';
            },
          });
        },
        error: (e: any) => {
          setError(e?.errorMessage ?? e?.reason ?? 'Errore inizializzazione SDK.');
          setState('error');
        },
      });
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Errore avvio meeting.');
      setState('error');
    }
  };

  // Inject #zmmtg-root if missing
  useEffect(() => {
    if (!document.getElementById('zmmtg-root')) {
      const el = document.createElement('div');
      el.id = 'zmmtg-root';
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    return () => {
      const root = document.getElementById('zmmtg-root');
      if (root) root.style.display = 'none';
    };
  }, []);

  return (
    <div ref={rootRef}>
      {(state === 'idle' || state === 'active') && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎥</div>
          <h3 style={{ color: 'var(--pub-navy)', fontFamily: 'var(--font-public)', fontWeight: 700, marginBottom: '0.5rem' }}>
            Meeting Zoom
          </h3>
          {state === 'idle' ? (
            <>
              <p style={{ color: 'var(--gray-500)', marginBottom: '1.5rem', fontSize: '0.9375rem' }}>
                Il meeting si aprirà in questa pagina.
              </p>
              <button onClick={join} className="btn-pub-cta" style={{ fontSize: '1rem' }}>
                ▶ Entra nel Meeting
              </button>
            </>
          ) : (
            <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
              Meeting in corso. Usa i controlli Zoom per lasciare la sessione.
            </p>
          )}
        </div>
      )}

      {state === 'loading' && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--gray-500)' }}>
          <div style={{ width: 40, height: 40, border: '3px solid var(--gray-200)', borderTopColor: 'var(--pub-navy)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <p>Connessione al meeting…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {state === 'error' && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div style={{ color: 'var(--danger)', marginBottom: '1rem' }}>❌ {error}</div>
          {joinUrl && (
            <a href={joinUrl} target="_blank" rel="noopener noreferrer" className="btn-pub-outline" style={{ textDecoration: 'none', marginRight: '0.5rem' }}>
              Apri in Zoom
            </a>
          )}
          <button onClick={() => setState('idle')} className="btn-pub-cta">Riprova</button>
        </div>
      )}
    </div>
  );
}
